import {
  CloseCircleOutlined,
  EditOutlined,
  HomeOutlined,
  LeftOutlined,
  RightOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Drawer,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareNumeric, compareText, speakerPhones, speechLabel } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  AssignmentCandidateRow,
  AssignmentConsideration,
  CongregationArchiveRow,
  ListResponse,
  LocalEventListResponse,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";
import { CandidateInfoPanel } from "../components/CandidateInfoPanel";
import { HostDialog } from "../components/HostDialog";
import { SpeakerListPreferences } from "../components/SpeakerListPreferences";
import { SpeakerName, speakerNameText } from "../components/SpeakerName";
import { WeekTimeDialog } from "../components/WeekTimeDialog";

/**
 * Sidebar entry "local_speech_assignations" → /local_assignation/speech_assignations.
 *
 * The public talks: which speaker holds which talk in which week. The calendar
 * on the left is the same one /local_assignation/assignations shows; here it is
 * read as "who is booked", and the drawer is where a week gets its speaker.
 *
 * A speaker is never assigned alone — a *pair* of speaker and talk is, because
 * the same speaker offers several talks and the congregation picks one of them.
 */
export function SpeechAssignationPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [assigningTo, setAssigningTo] = useState<CongregationArchiveRow | null>(null);
  const [hostOf, setHostOf] = useState<CongregationArchiveRow | null>(null);
  const [editing, setEditing] = useState<CongregationArchiveRow | null>(null);

  const events = useQuery({
    queryKey: ["local_assignation", "events"],
    queryFn: () => apiFetch<LocalEventListResponse>("/local_assignation/events"),
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
    // The same weeks are what the congregation's calendar and its archive show.
    void queryClient.invalidateQueries({ queryKey: ["archive"] });
  }, [queryClient]);

  // Takes the booking back: speaker, talk and host go with the week's event —
  // the same action the congregation's calendar offers, and the server sends
  // the speaker the cancellation.
  const cancelEvent = useMutation({
    mutationFn: (week: string) =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  // Who is putting the speaker up — or, until somebody is named, why not yet.
  // Read twice: once for the cell, once for the export.
  const hostLabel = useCallback(
    (row: CongregationArchiveRow): string | null =>
      row.hospitality_host_name ??
      (row.hospitality_status === "not_provided"
        ? t("local_assignation.hospitality_not_provided")
        : row.hospitality_status
          ? t("local_assignation.hospitality_not_established")
          : null),
    [t],
  );

  const columns = useMemo<ExportableColumn<CongregationArchiveRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        fixed: "left",
        // The tag goes under the date instead of beside it: side by side the
        // two share the column's width and the date is what breaks.
        render: (value: string, row) => (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {row.required_substitution && (
              <Tag color="red" style={{ marginInlineEnd: 0 }}>
                {t("local_assignation.required_substitution")}
              </Tag>
            )}
          </Space>
        ),
        exportValue: (row) =>
          [
            row.week_label,
            row.required_substitution ? t("local_assignation.required_substitution") : null,
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 190,
        // The tag says what kind of event this is, so it belongs to the event
        // and not to the week it falls in — it stands in front of the name of
        // the event the way an adjective does. No ellipsis on the column: it
        // would cut the tag and the name together into one truncated line.
        render: (value: string | null, row) =>
          row.is_circumscription_event ? (
            <Space size={4} wrap>
              <Tooltip title={t("speaker_assignation.circumscription_requested")}>
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {t("activerecord.models.circumscription")}
                </Tag>
              </Tooltip>
              <span>{value}</span>
            </Space>
          ) : (
            value
          ),
        exportValue: (row) =>
          [
            row.is_circumscription_event ? t("activerecord.models.circumscription") : null,
            row.event_type_label,
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        width: 200,
        ellipsis: true,
      },
      {
        // The number of the outline and the title of the talk in one cell, see
        // speechLabel — on its own the number said little and took the width
        // the titles needed.
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        width: 260,
        ellipsis: true,
        render: (_value: string | null, row: CongregationArchiveRow) =>
          speechLabel(row.speech_schema, row.speech_title, t),
        exportValue: (row: CongregationArchiveRow) =>
          speechLabel(row.speech_schema, row.speech_title, t),
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker_name",
        width: 180,
        ellipsis: true,
        render: (_value: string | null, row) => (
          <SpeakerName
            name={row.speaker_name}
            contactMe={row.speaker_contact_me}
            hint={t("speaker.contact_me")}
          />
        ),
        exportValue: (row) => speakerNameText(row.speaker_name, row.speaker_contact_me, t),
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        // Both numbers of the account, one under the other: which of the two a
        // speaker keeps is up to them, and beside each other they would need
        // the width of two columns to stay readable.
        title: t("activerecord.attributes.user.phone"),
        key: "speaker_phone",
        width: 150,
        render: (_value: unknown, row) => (
          <Space orientation="vertical" size={0} align="start">
            {speakerPhones(row).map((number) => (
              <span key={number} style={{ whiteSpace: "nowrap" }}>
                {number}
              </span>
            ))}
          </Space>
        ),
        // No dataIndex to fall back on: the cell is built from two fields.
        exportValue: (row) => speakerPhones(row).join(" · ") || null,
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "speaker_email",
        width: 200,
        ellipsis: true,
      },
      {
        // Says whether the week still needs a host, which is what the button
        // beside it is for.
        title: t("activerecord.attributes.local_assignation.speaker_host_name"),
        dataIndex: "hospitality_host_name",
        width: 160,
        ellipsis: true,
        render: (_value: string | null, row) => hostLabel(row),
        exportValue: (row) => hostLabel(row),
      },
      {
        key: "actions",
        fixed: "right",
        width: 165,
        render: (_value, row) => (
          <Space size={4}>
            {/* Day and time of this one week. The old screen let both be typed
                straight into the grid; the event type is not among them because
                the server keeps it once the week has an assignation. */}
            {row.has_event && (
              <Tooltip title={t("form.update")}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={row.is_circumscription_event || cancelEvent.isPending}
                  onClick={() => setEditing(row)}
                />
              </Tooltip>
            )}

            <Tooltip title={t("local_assignation.select_speaker")}>
              <Button
                size="small"
                icon={<UserAddOutlined />}
                // The zone owns its own weeks, and a week already taken by
                // something other than a public talk is not on offer either.
                disabled={row.is_circumscription_event || cancelEvent.isPending}
                onClick={() => setAssigningTo(row)}
              />
            </Tooltip>

            {/* Only a week with a visiting speaker can have a host, and only
                for a speaker who wants one — hospitality_status stays null for
                the others, and the server refuses them. */}
            {row.speaker_name && (
              <Tooltip
                title={
                  row.hospitality_status
                    ? t("local_assignation.host_assign")
                    : t("local_assignation.hospitality_speaker_not_required")
                }
              >
                <Button
                  size="small"
                  icon={<HomeOutlined />}
                  disabled={!row.hospitality_status || cancelEvent.isPending}
                  onClick={() => setHostOf(row)}
                />
              </Tooltip>
            )}

            {row.speaker_name && (
              <Tooltip title={t("local_assignation.cancel")}>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  disabled={row.is_circumscription_event || cancelEvent.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: t("dialog.confirmation_title"),
                      content: (
                        <>
                          <Typography.Paragraph>
                            {[row.week_label, row.speaker_name].filter(Boolean).join(" · ")}
                          </Typography.Paragraph>
                          {t("local_assignation.confirm_cancel_local_event")}
                        </>
                      ),
                      okText: t("dialog.answer_yes"),
                      okButtonProps: { danger: true },
                      cancelText: t("dialog.answer_no"),
                      onOk: () => cancelEvent.mutate(row.week_begin),
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, cancelEvent, modal, hostLabel],
  );

  return (
    <Card title={t("navigation.local_speech_assignations")}>
      <DataTable<CongregationArchiveRow>
        columns={columns}
        rows={events.data?.data}
        exportAs={t("navigation.local_speech_assignations")}
        loading={events.isPending || cancelEvent.isPending}
        error={events.error}
        rowKey="week_begin"
        rowClassName={(row) =>
          row.required_substitution
            ? "speech-week-substitution"
            : row.speaker_name
              ? ""
              : "speech-week-open"
        }
      />

      <style>{`
        .speech-week-open { background: rgba(250, 173, 20, 0.06); }
        .speech-week-substitution { background: rgba(255, 77, 79, 0.08); }
      `}</style>

      {assigningTo && (
        <CandidatesDrawer week={assigningTo} onClose={() => setAssigningTo(null)} />
      )}

      {hostOf && (
        <HostDialog week={hostOf} onClose={() => setHostOf(null)} onChanged={refresh} />
      )}

      {editing && (
        <WeekTimeDialog week={editing} onClose={() => setEditing(null)} onChanged={refresh} />
      )}
    </Card>
  );
}

/**
 * The speaker/talk pairs on offer for one week. Picking one asks the server
 * first what speaks against it — an inactive talk, a journey beyond the
 * speaker's range, too little notice — and shows those before going ahead. The
 * old screen did the same through a confirmation dialog; the difference is that
 * the reasons now arrive as data instead of as markup.
 */
function CandidatesDrawer({
  week,
  onClose,
}: {
  week: CongregationArchiveRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const candidates = useQuery({
    queryKey: ["local_assignation", "candidates", week.week_begin],
    queryFn: () =>
      apiFetch<ListResponse<AssignmentCandidateRow>>(
        `/local_assignation/events/${week.week_begin}/candidates`,
      ),
  });

  // Which candidate the panel on the right describes, and whether that panel is
  // showing at all. The old screen kept it collapsed until it was wanted; so
  // does this one, and the choice survives switching candidates.
  const [selected, setSelected] = useState<AssignmentCandidateRow | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const assign = useMutation({
    mutationFn: (candidate: AssignmentCandidateRow) =>
      apiFetch<NotificationsResponse>(
        `/local_assignation/events/${week.week_begin}/assignment`,
        { method: "PUT", body: { speaker_speech_id: candidate.id } },
      ),
    onSuccess: (data) => {
      notify(data.notifications);
      onClose();
      void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
      void queryClient.invalidateQueries({ queryKey: ["archive"] });
    },
    onError: showError,
  });

  /** Ask about the pair, then either assign straight away or lay out the doubts. */
  const pick = useCallback(
    async (candidate: AssignmentCandidateRow) => {
      let considerations: AssignmentConsideration[] = [];

      try {
        const answer = await apiFetch<ListResponse<AssignmentConsideration>>(
          `/local_assignation/events/${week.week_begin}/candidates/${candidate.id}`,
        );
        considerations = answer.data;
      } catch (error) {
        // The considerations are advisory; failing to read them must not block
        // the assignment, so this only reports and carries on.
        showError(error);
      }

      if (considerations.length === 0) {
        assign.mutate(candidate);
        return;
      }

      modal.confirm({
        title: t("dialog.considerations"),
        width: 620,
        content: (
          <Space orientation="vertical" size="middle" style={{ marginTop: 12 }}>
            {considerations.map((item) => (
              <Alert
                key={item.key}
                type="warning"
                showIcon
                title={item.title}
                description={item.text}
              />
            ))}
          </Space>
        ),
        okText: t("dialog.answer_yes"),
        cancelText: t("dialog.answer_no"),
        onOk: () => assign.mutate(candidate),
      });
    },
    [week.week_begin, showError, assign, modal, t],
  );

  const columns = useMemo<TableProps<AssignmentCandidateRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 140,
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 140,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "speaker_circumscription",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
        sorter: (a, b) => compareNumeric(a.speech_schema, b.speech_schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "speech_language",
        width: 90,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.speaker_speech.additional_notes"),
        dataIndex: "additional_notes",
        width: 200,
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_value, row) => (
          <Tooltip title={t("local_assignation.select_speaker")}>
            <Button
              size="small"
              type="primary"
              icon={<UserAddOutlined />}
              disabled={assign.isPending}
              // The row itself opens the info panel; assigning is its own act.
              onClick={(event) => {
                event.stopPropagation();
                void pick(row);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [t, assign.isPending, pick],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={panelOpen ? "min(1600px, 96vw)" : "min(1100px, 96vw)"}
      destroyOnHidden
      title={
        <Typography.Text>
          {[week.week_label, week.week_day_label, week.day_time].filter(Boolean).join(" · ")}
        </Typography.Text>
      }
      extra={
        <Button
          icon={panelOpen ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setPanelOpen((open) => !open)}
        >
          {t("local_assignation.search_speaker_info")}
        </Button>
      }
    >
      <SpeakerListPreferences disabled={assign.isPending} />

      {/* List and panel side by side, as the old layout had them. `minWidth: 0`
          keeps the table from pushing the panel out: without it a flex child
          refuses to shrink below its content and the table scrolls the page
          instead of itself. */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DataTable<AssignmentCandidateRow>
            columns={columns}
            rows={candidates.data?.data}
            exportAs={[week.week_label, t("speech.speakers")].filter(Boolean).join(" · ")}
            loading={candidates.isFetching || assign.isPending}
            error={candidates.error}
            onRowClick={(row) => {
              setSelected(row);
              setPanelOpen(true);
            }}
            rowClassName={(row) => (row.id === selected?.id ? "candidate-selected" : "")}
          />
        </div>

        {panelOpen && (
          <div
            style={{
              width: 420,
              flexShrink: 0,
              borderInlineStart: "1px solid #f0f0f0",
              paddingInlineStart: 16,
              maxHeight: "calc(100vh - 200px)",
              overflow: "auto",
            }}
          >
            <CandidateInfoPanel
              weekBegin={week.week_begin}
              speakerSpeechId={selected?.id ?? null}
              placeholder={t("local_assignation.select_speaker")}
            />
          </div>
        )}
      </div>

      <style>{`.candidate-selected > td { background: rgba(22, 119, 255, 0.06); }`}</style>
    </Drawer>
  );
}
